import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Building2, Users2, Contact2, FileCheck2, ChevronRight, ChevronLeft,
  Save, Send, Upload, Check, X, AlertTriangle, FileText, Loader2, Trash2,
  CheckCircle2,
} from "lucide-react";

const SECTIONS = [
  { id: "entity", label: "Legal Entity", icon: Building2 },
  { id: "signatories", label: "Signatories", icon: Users2 },
  { id: "contacts", label: "Point of Contact", icon: Contact2 },
  { id: "documents", label: "Documents", icon: FileCheck2 },
];

// ---- Validation helpers --------------------------------------------------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+\..+$/i;
const LINKEDIN_RE = /^https?:\/\/(www\.)?linkedin\.com\/.+/i;
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const isValidEmail = (v) => EMAIL_RE.test((v || "").trim());
const isValidPhone = (v) => {
  const d = onlyDigits(v);
  return d.length >= 10 && d.length <= 15;
};
const isValidUrl = (v) => URL_RE.test((v || "").trim());
const isValidLinkedIn = (v) => !v || LINKEDIN_RE.test((v || "").trim());
const nonEmpty = (v) => !!(v || "").trim();

const emptySignatory = () => ({ name: "", title: "", email: "", linkedin: "", phone: "", mobile: "" });
const emptyContact = () => ({ name: "", title: "", email: "", linkedin: "", phone: "", mobile: "" });
const emptyEntity = () => ({
  legal_name: "", incorporation_number: "", country_of_incorporation: "",
  company_email: "", company_phone: "", company_website: "", correspondence_address: "",
});

export default function ProspectiveOnboardingForm() {
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("entity");
  const [entity, setEntity] = useState(emptyEntity());
  const [signatories, setSignatories] = useState([emptySignatory()]);
  const [contacts, setContacts] = useState([emptyContact()]);
  const [docCatalog, setDocCatalog] = useState([]);
  const [documents, setDocuments] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const dirtyRef = useRef(false);

  const readOnly = sub?.status === "submitted" || sub?.status === "approved";

  useEffect(() => {
    // Wait for auth context to finish hydrating to avoid a race where the
    // API call fires before the access token is in memory.
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [{ data: s }, { data: c }] = await Promise.all([
          API.get("/onboarding/me"),
          API.get("/onboarding-catalog/documents"),
        ]);
        if (cancelled) return;
        if (s.status === "submitted" || s.status === "approved") {
          navigate("/onboarding/review", { replace: true });
          return;
        }
        setSub(s);
        if (s.legal_entity && Object.keys(s.legal_entity).length) setEntity({ ...emptyEntity(), ...s.legal_entity });
        if (s.signatories?.length) setSignatories(s.signatories);
        if (s.contacts?.length) setContacts(s.contacts);
        setDocuments(s.documents || {});
        setDocCatalog(c);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load form. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, authLoading, isAuthenticated]);

  const allDocKeys = useMemo(() => docCatalog.flatMap((s) => s.items.map((i) => i.key)), [docCatalog]);
  const requiredDocKeys = useMemo(() => docCatalog.flatMap((s) => s.items.filter((i) => i.required).map((i) => i.key)), [docCatalog]);

  // ---- Per-section validity + completion ---------------------------------
  const entityErrors = useMemo(() => {
    const e = {};
    if (!nonEmpty(entity.legal_name)) e.legal_name = "Required";
    if (!nonEmpty(entity.incorporation_number)) e.incorporation_number = "Required";
    if (!nonEmpty(entity.country_of_incorporation)) e.country_of_incorporation = "Required";
    if (!nonEmpty(entity.company_email)) e.company_email = "Required";
    else if (!isValidEmail(entity.company_email)) e.company_email = "Invalid email format";
    if (!nonEmpty(entity.company_phone)) e.company_phone = "Required";
    else if (!isValidPhone(entity.company_phone)) e.company_phone = "10–15 digits required";
    if (!nonEmpty(entity.company_website)) e.company_website = "Required";
    else if (!isValidUrl(entity.company_website)) e.company_website = "Must start with http(s)://";
    if (!nonEmpty(entity.correspondence_address)) e.correspondence_address = "Required";
    return e;
  }, [entity]);

  const partyErrors = (p) => {
    const e = {};
    if (!nonEmpty(p.name)) e.name = "Required";
    if (!nonEmpty(p.title)) e.title = "Required";
    if (!nonEmpty(p.email)) e.email = "Required";
    else if (!isValidEmail(p.email)) e.email = "Invalid email format";
    if (!nonEmpty(p.phone)) e.phone = "Required";
    else if (!isValidPhone(p.phone)) e.phone = "10–15 digits required";
    if (!nonEmpty(p.mobile)) e.mobile = "Required";
    else if (!isValidPhone(p.mobile)) e.mobile = "10–15 digits required";
    if (p.linkedin && !isValidLinkedIn(p.linkedin)) e.linkedin = "Must be a LinkedIn URL";
    return e;
  };

  const signatoryErrorsList = useMemo(() => signatories.map(partyErrors), [signatories]);
  const contactErrorsList = useMemo(() => contacts.map(partyErrors), [contacts]);

  const sectionStats = useMemo(() => {
    // Entity: 7 required fields
    const entityTotal = 7;
    const entityDone = entityTotal - Object.keys(entityErrors).length;

    // Signatories: 5 required per signatory × N
    const sigTotal = signatories.length * 5;
    const sigDone = signatories.reduce((acc, _s, i) => {
      const errs = signatoryErrorsList[i] || {};
      const requiredErrs = ["name", "title", "email", "phone", "mobile"].filter((k) => errs[k]).length;
      return acc + (5 - requiredErrs);
    }, 0);

    // Contacts: 5 required per contact × N
    const conTotal = contacts.length * 5;
    const conDone = contacts.reduce((acc, _c, i) => {
      const errs = contactErrorsList[i] || {};
      const requiredErrs = ["name", "title", "email", "phone", "mobile"].filter((k) => errs[k]).length;
      return acc + (5 - requiredErrs);
    }, 0);

    // Documents: each required doc counts
    const docTotal = requiredDocKeys.length;
    const docDone = requiredDocKeys.filter((k) => documents[k]).length;

    const pct = (done, total) => (total === 0 ? 0 : Math.round((done / total) * 100));
    return {
      entity: { done: entityDone, total: entityTotal, pct: pct(entityDone, entityTotal) },
      signatories: { done: sigDone, total: sigTotal, pct: pct(sigDone, sigTotal) },
      contacts: { done: conDone, total: conTotal, pct: pct(conDone, conTotal) },
      documents: { done: docDone, total: docTotal, pct: pct(docDone, docTotal) },
    };
  }, [entityErrors, signatoryErrorsList, contactErrorsList, signatories, contacts, documents, requiredDocKeys]);

  const completion = useMemo(() => {
    const totals = sectionStats.entity.total + sectionStats.signatories.total + sectionStats.contacts.total + sectionStats.documents.total;
    const done = sectionStats.entity.done + sectionStats.signatories.done + sectionStats.contacts.done + sectionStats.documents.done;
    return totals === 0 ? 0 : Math.round((done / totals) * 100);
  }, [sectionStats]);

  const allValid = sectionStats.entity.pct === 100 && sectionStats.signatories.pct === 100 && sectionStats.contacts.pct === 100 && sectionStats.documents.pct === 100;

  const persist = async ({ silent = false } = {}) => {
    if (readOnly) return;
    setSaving(true);
    try {
      const payload = {
        legal_entity: entity,
        signatories,
        contacts,
      };
      const { data } = await API.put("/onboarding/me", payload);
      setSub(data);
      dirtyRef.current = false;
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (key, file) => {
    if (!file) return;
    setDocuments((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), uploading: true } }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await API.post(`/onboarding/me/documents/${key}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocuments((prev) => ({ ...prev, [key]: data }));
      toast.success("Uploaded");
    } catch (e) {
      setDocuments((prev) => { const n = { ...prev }; delete n[key]?.uploading; return n; });
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const deleteDoc = async (key) => {
    try {
      await API.delete(`/onboarding/me/documents/${key}`);
      setDocuments((prev) => { const n = { ...prev }; delete n[key]; return n; });
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const handleSubmit = async () => {
    if (!allValid) {
      // Force show all errors
      const allTouched = {};
      ["legal_name", "incorporation_number", "country_of_incorporation", "company_email", "company_phone", "company_website", "correspondence_address"].forEach((k) => { allTouched[`entity.${k}`] = true; });
      signatories.forEach((_s, i) => ["name", "title", "email", "phone", "mobile", "linkedin"].forEach((k) => { allTouched[`signatory.${i}.${k}`] = true; }));
      contacts.forEach((_c, i) => ["name", "title", "email", "phone", "mobile", "linkedin"].forEach((k) => { allTouched[`contact.${i}.${k}`] = true; }));
      setTouched(allTouched);
      toast.error("Please fix all errors before submitting");
      return;
    }
    // Always persist latest fields before submitting
    await persist({ silent: true });
    setSubmitting(true);
    try {
      await API.post("/onboarding/me/submit");
      toast.success("Submitted for review!");
      navigate("/onboarding/review", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const goToSection = (id) => setActiveSection(id);
  const idx = SECTIONS.findIndex((s) => s.id === activeSection);
  const isFirst = idx === 0;
  const isLast = idx === SECTIONS.length - 1;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="onboarding-form-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Client Onboarding</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Tell us about your fund</h1>
          <p className="text-sm text-muted-foreground mt-1.5">Complete every section. Drafts auto-save when you click "Save Draft".</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs px-3 py-1.5" data-testid="completion-badge">{completion}% complete</Badge>
          {!readOnly && (
            <Button variant="outline" onClick={() => persist()} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Draft
            </Button>
          )}
        </div>
      </div>

      {sub?.status === "needs_revision" && sub?.revision_notes && (
        <Card className="border-rose-500/40 bg-rose-500/5">
          <CardContent className="py-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-rose-500 text-sm">Revision requested</p>
              <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{sub.revision_notes}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        {/* Stepper */}
        <Card className="h-fit lg:sticky lg:top-20" data-testid="onboarding-stepper">
          <CardContent className="p-3">
            <nav className="space-y-1">
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                const isActive = activeSection === s.id;
                const stat = sectionStats[s.id];
                const isComplete = stat?.pct === 100;
                return (
                  <button
                    key={s.id}
                    onClick={() => goToSection(s.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-secondary/60 text-muted-foreground"}`}
                    data-testid={`step-${s.id}`}
                  >
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${isComplete ? "bg-emerald-500 text-white" : isActive ? "bg-primary-foreground/20" : "bg-secondary"}`}>
                      {isComplete ? <Check className="w-4 h-4" /> : i + 1}
                    </span>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="truncate">{s.label}</p>
                      <p className={`text-[10px] ${isActive ? "text-primary-foreground/70" : "text-muted-foreground"}`} data-testid={`step-${s.id}-pct`}>{stat?.pct ?? 0}%</p>
                    </div>
                  </button>
                );
              })}
            </nav>
            <Separator className="my-3" />
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Need help?</p>
              <p className="leading-relaxed">Raise a support ticket and our team will get back to you.</p>
            </div>
          </CardContent>
        </Card>

        {/* Active Section */}
        <div className="space-y-6">
          {activeSection === "entity" && (
            <Card data-testid="section-entity">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" /> Legal Entity Details</CardTitle>
                <CardDescription>Tell us about the legal entity setting up the fund.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FieldText label="Name of Legal Entity" required value={entity.legal_name} onChange={(v) => setEntity({ ...entity, legal_name: v })} testid="entity-legal-name" error={touched["entity.legal_name"] && entityErrors.legal_name} onBlur={() => setTouched({ ...touched, "entity.legal_name": true })} />
                <FieldText label="Incorporation Number" required value={entity.incorporation_number} onChange={(v) => setEntity({ ...entity, incorporation_number: v })} testid="entity-incorp-number" error={touched["entity.incorporation_number"] && entityErrors.incorporation_number} onBlur={() => setTouched({ ...touched, "entity.incorporation_number": true })} />
                <FieldText label="Country of Incorporation" required value={entity.country_of_incorporation} onChange={(v) => setEntity({ ...entity, country_of_incorporation: v })} testid="entity-country" error={touched["entity.country_of_incorporation"] && entityErrors.country_of_incorporation} onBlur={() => setTouched({ ...touched, "entity.country_of_incorporation": true })} />
                <FieldText label="Company Email" type="email" required value={entity.company_email} onChange={(v) => setEntity({ ...entity, company_email: v })} testid="entity-email" error={touched["entity.company_email"] && entityErrors.company_email} onBlur={() => setTouched({ ...touched, "entity.company_email": true })} />
                <FieldText label="Company Phone" required value={entity.company_phone} onChange={(v) => setEntity({ ...entity, company_phone: v })} testid="entity-phone" error={touched["entity.company_phone"] && entityErrors.company_phone} onBlur={() => setTouched({ ...touched, "entity.company_phone": true })} inputMode="tel" placeholder="e.g. +1 555 123 4567" />
                <FieldText label="Company Website" required value={entity.company_website} onChange={(v) => setEntity({ ...entity, company_website: v })} testid="entity-website" placeholder="https://" error={touched["entity.company_website"] && entityErrors.company_website} onBlur={() => setTouched({ ...touched, "entity.company_website": true })} />
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-sm">Correspondence Address <span className="text-rose-500">*</span></Label>
                  <Textarea
                    rows={3}
                    value={entity.correspondence_address}
                    onChange={(e) => setEntity({ ...entity, correspondence_address: e.target.value })}
                    onBlur={() => setTouched({ ...touched, "entity.correspondence_address": true })}
                    placeholder="Suite, street, city, state, country"
                    data-testid="entity-address"
                  />
                  {touched["entity.correspondence_address"] && entityErrors.correspondence_address && (
                    <p className="text-xs text-rose-500">{entityErrors.correspondence_address}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === "signatories" && (
            <Card data-testid="section-signatories">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2"><Users2 className="w-5 h-5 text-primary" /> Authorized Signatories</CardTitle>
                <CardDescription>Add one or two authorized signatories.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm">Number of Signatories</Label>
                  <RadioGroup
                    value={String(signatories.length)}
                    onValueChange={(v) => {
                      const n = Number(v);
                      setSignatories((prev) => {
                        if (n === 1) return [prev[0] || emptySignatory()];
                        return [prev[0] || emptySignatory(), prev[1] || emptySignatory()];
                      });
                    }}
                    className="flex gap-4 mt-2"
                    data-testid="signatories-count"
                  >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="1" /> <span className="text-sm">One</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="2" /> <span className="text-sm">Two</span>
                    </label>
                  </RadioGroup>
                </div>
                {signatories.map((s, i) => (
                  <PartyCard
                    key={i}
                    title={`Authorized Signatory ${i + 1}`}
                    value={s}
                    onChange={(v) => {
                      const next = [...signatories];
                      next[i] = v;
                      setSignatories(next);
                    }}
                    testidPrefix={`signatory-${i}`}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {activeSection === "contacts" && (
            <Card data-testid="section-contacts">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2"><Contact2 className="w-5 h-5 text-primary" /> Primary Point of Contact</CardTitle>
                <CardDescription>Can be the same as an authorized signatory. Add more if needed.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {contacts.map((c, i) => (
                  <PartyCard
                    key={i}
                    title={`Point of Contact ${i + 1}`}
                    value={c}
                    errors={contactErrorsList[i] || {}}
                    touched={touched}
                    onBlur={(field) => setTouched({ ...touched, [`contact.${i}.${field}`]: true })}
                    touchedPrefix={`contact.${i}.`}
                    onChange={(v) => {
                      const next = [...contacts];
                      next[i] = v;
                      setContacts(next);
                    }}
                    onRemove={i > 0 ? () => setContacts(contacts.filter((_, j) => j !== i)) : null}
                    testidPrefix={`contact-${i}`}
                    copyFromOptions={signatories.map((s, si) => ({
                      label: `Same as Authorized Signatory ${si + 1}${s.name ? ` (${s.name})` : ""}`,
                      value: s,
                      disabled: !((s.name || "").trim() || (s.email || "").trim()),
                    }))}
                  />
                ))}
                {contacts.length < 4 && (
                  <Button variant="outline" size="sm" onClick={() => setContacts([...contacts, emptyContact()])} data-testid="add-contact-btn">
                    + Add another point of contact
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {activeSection === "documents" && (
            <Card data-testid="section-documents">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-primary" /> Legal & KYC Documents</CardTitle>
                <CardDescription>Upload each document. Required documents are marked with an asterisk. Max 25 MB per file.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {docCatalog.map((section) => (
                  <div key={section.section}>
                    <h3 className="text-sm font-semibold text-foreground mb-3">{section.section}</h3>
                    <div className="space-y-2">
                      {section.items.map((item) => (
                        <DocRow
                          key={item.key}
                          item={item}
                          doc={documents[item.key]}
                          onUpload={(file) => uploadDoc(item.key, file)}
                          onDelete={() => deleteDoc(item.key)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3">
            <Button variant="outline" disabled={isFirst} onClick={() => goToSection(SECTIONS[idx - 1].id)} data-testid="prev-step-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <div className="text-xs text-muted-foreground">Step {idx + 1} of {SECTIONS.length}</div>
            {!isLast ? (
              <Button onClick={() => goToSection(SECTIONS[idx + 1].id)} data-testid="next-step-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-form-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Submit for Review
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldText({ label, required, value, onChange, type = "text", testid, placeholder, error, onBlur, inputMode }) {
  return (
    <div className="space-y-2 min-w-0">
      <Label className="text-sm">{label} {required && <span className="text-rose-500">*</span>}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        data-testid={testid}
        placeholder={placeholder}
        inputMode={inputMode}
        className={error ? "border-rose-500/60 focus-visible:ring-rose-500/40" : ""}
      />
      {error && <p className="text-xs text-rose-500" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function PartyCard({ title, value, onChange, onRemove, testidPrefix, errors = {}, touched = {}, onBlur, touchedPrefix = "", copyFromOptions = null }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const errIf = (k) => (touched[`${touchedPrefix}${k}`] ? errors[k] : null);
  const blur = (k) => { if (onBlur) onBlur(k); };
  const copyFrom = (party) => {
    onChange({
      name: party.name || "",
      title: party.title || "",
      email: party.email || "",
      linkedin: party.linkedin || "",
      phone: party.phone || "",
      mobile: party.mobile || "",
    });
  };
  return (
    <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-3" data-testid={testidPrefix}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-medium">{title}</p>
        <div className="flex items-center gap-2">
          {copyFromOptions && copyFromOptions.length > 0 && (
            <select
              className="text-xs bg-background border border-border rounded-md px-2 py-1.5 text-foreground"
              defaultValue=""
              onChange={(e) => {
                const idx = Number(e.target.value);
                if (!Number.isNaN(idx) && copyFromOptions[idx]) {
                  copyFrom(copyFromOptions[idx].value);
                }
                e.target.value = "";
              }}
              data-testid={`${testidPrefix}-copy-from`}
            >
              <option value="" disabled>Copy from signatory…</option>
              {copyFromOptions.map((opt, idx) => (
                <option key={idx} value={idx} disabled={opt.disabled}>{opt.label}</option>
              ))}
            </select>
          )}
          {onRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove} className="text-rose-500" data-testid={`${testidPrefix}-remove`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldText label="Full Name" required value={value.name} onChange={(v) => set("name", v)} testid={`${testidPrefix}-name`} error={errIf("name")} onBlur={() => blur("name")} />
        <FieldText label="Title / Designation" required value={value.title} onChange={(v) => set("title", v)} testid={`${testidPrefix}-title`} error={errIf("title")} onBlur={() => blur("title")} />
        <FieldText label="Email" type="email" required value={value.email} onChange={(v) => set("email", v)} testid={`${testidPrefix}-email`} error={errIf("email")} onBlur={() => blur("email")} />
        <FieldText label="LinkedIn (optional)" value={value.linkedin} onChange={(v) => set("linkedin", v)} testid={`${testidPrefix}-linkedin`} placeholder="https://linkedin.com/in/..." error={errIf("linkedin")} onBlur={() => blur("linkedin")} />
        <FieldText label="Phone" required value={value.phone} onChange={(v) => set("phone", v)} testid={`${testidPrefix}-phone`} inputMode="tel" placeholder="e.g. +1 555 123 4567" error={errIf("phone")} onBlur={() => blur("phone")} />
        <FieldText label="Mobile" required value={value.mobile} onChange={(v) => set("mobile", v)} testid={`${testidPrefix}-mobile`} inputMode="tel" placeholder="e.g. +1 555 987 6543" error={errIf("mobile")} onBlur={() => blur("mobile")} />
      </div>
    </div>
  );
}

function DocRow({ item, doc, onUpload, onDelete }) {
  const inputRef = useRef(null);
  const uploaded = !!doc?.stored_name;
  const uploading = !!doc?.uploading;

  return (
    <div className="flex items-start sm:items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:bg-secondary/30 flex-wrap sm:flex-nowrap" data-testid={`doc-${item.key}`}>
      <FileText className={`w-4 h-4 flex-shrink-0 mt-0.5 sm:mt-0 ${uploaded ? "text-emerald-500" : "text-muted-foreground"}`} />
      <div className="flex-1 min-w-0 basis-full sm:basis-auto">
        <p className="text-sm font-medium leading-snug break-words">
          {item.label} {item.required && <span className="text-rose-500">*</span>}
        </p>
        {uploaded && (
          <p className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`doc-${item.key}-filename`}>{doc.file_name} • {(doc.size / 1024).toFixed(1)} KB</p>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = "";
        }}
        data-testid={`doc-${item.key}-input`}
      />
      {uploaded ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 text-[10px]">
            <Check className="w-3 h-3 mr-1" /> Uploaded
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} data-testid={`doc-${item.key}-replace`}>
            Replace
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} className="text-rose-500 hover:text-rose-500" data-testid={`doc-${item.key}-delete`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="flex-shrink-0" data-testid={`doc-${item.key}-upload`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />} Upload
        </Button>
      )}
    </div>
  );
}
